<template>
  <div
    class="m-auto text-xs"
    :class="
      lines.length > 2 ? 'text-xs' : lines.length > 1 ? 'text-sm' : 'text-base'
    "
  >
    <div
      v-for="(line, i) in lines"
      :key="i"
      class="m-auto flex items-center justify-center gap-1"
    >
      <MaybeIcon v-for="(value, j) in line" :key="j" :value="value" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { assertUnreachable } from "@/utils";
import {
  Inc,
  CustomAction,
  isCustomConsumer,
  isCustomFirmware,
  isCustomLedCycle,
  isCustomLedBrightness,
  isCustomMouseClick,
  isCustomMouseMove,
  isCustomMouseSensitivity,
  flattenCustomAction,
} from "@/keyboard";
import { useKeycapStore } from "@/stores";
import MaybeIcon from "@/components/utils/MaybeIcon.vue";

const kc = useKeycapStore();

const props = defineProps<{
  action: CustomAction;
}>();

type IconOrText = { icon: string } | string;

const lines = computed((): IconOrText[][] => {
  const lines = [];

  // Custom.Led  .Brightness=Up
  // Custom.Led  .Cycle=Up
  // Custom.Mouse.Click=Left
  // Custom.Mouse.Move=Left
  // Custom.Mouse.Sensitivity=Up
  // Custom.Consumer=ConsumerKey
  // Custom.Firmware=FirmwareAction
  const act = flattenCustomAction({ Custom: props.action });

  const getInc = (i: Inc): IconOrText => {
    if (i == "Up") return "+";
    else if (i == "Down") return "-";
    else return assertUnreachable(i);
  };

  if (isCustomMouseClick(act)) {
    // Mouse Click (comments to fight auto-formatting that removes all newlines)

    if (kc.use_icons) {
      lines.push([{ icon: "ic:outline-mouse" }]);
      lines.push([act.Click]);
    } else {
      lines.push(["Mouse"]);
      lines.push(["Click"]);
      lines.push([act.Click]);
    }

    // Mouse Click End
  } else if (isCustomMouseMove(act)) {
    // Mouse Move

    if (kc.use_icons) {
      // Mouse Move: with icons
      const m = act.Move;
      const arrow =
        m == "Up" || m == "WheelUp"
          ? "mdi:arrow-up"
          : m == "Down" || m == "WheelDown"
          ? "mdi:arrow-down"
          : m == "Left" || m == "PanLeft"
          ? "mdi:arrow-left"
          : m == "Right" || m == "PanRight"
          ? "mdi:arrow-right"
          : assertUnreachable(m);

      lines.push([{ icon: "ic:outline-mouse" }, { icon: arrow }]);

      if (m == "WheelUp" || m == "WheelDown") lines.push(["Wheel"]);
      else if (m == "PanLeft" || m == "PanRight") lines.push(["Pan"]);
    } else {
      // Mouse Move: no icons
      lines.push(["Mouse"]);
      if (act.Move == "WheelUp" || act.Move == "WheelDown") {
        lines.push(["Wheel"]);
        if (act.Move == "WheelUp") lines.push(["Up"]);
        else lines.push(["Down"]);
      } else if (act.Move == "PanLeft" || act.Move == "PanRight") {
        lines.push(["Pan"]);
        if (act.Move == "PanLeft") lines.push(["Left"]);
        else lines.push(["Right"]);
      } else {
        lines.push(["Move"]);
        lines.push([act.Move]);
      }
    }

    // Mouse Move End
  } else if (isCustomMouseSensitivity(act)) {
    // Mouse Sensitivity

    if (kc.use_icons) {
      lines.push([{ icon: "ic:outline-mouse" }, getInc(act.Sensitivity)]);
    } else {
      lines.push(["Mouse"]);
      lines.push(["Sens", getInc(act.Sensitivity)]);
    }

    // Mouse Sensitivity End
  } else if (isCustomLedBrightness(act)) {
    // Led Brightness

    if (kc.use_icons) {
      lines.push([{ icon: "ic:outline-light-mode" }, getInc(act.Brightness)]);
    } else {
      lines.push(["Led"]);
      lines.push(["Br", getInc(act.Brightness)]);
    }

    // Led Brightness End
  } else if (isCustomLedCycle(act)) {
    // Led Cycle

    if (kc.use_icons) {
      const arrow =
        act.Cycle == "Down"
          ? "ph:arrow-arc-left"
          : act.Cycle == "Up"
          ? "ph:arrow-arc-right"
          : assertUnreachable(act.Cycle);
      lines.push([{ icon: "ic:outline-light-mode" }, { icon: arrow }]);
    } else {
      lines.push(["Led"]);
      if (act.Cycle == "Down") lines.push(["Prev"]);
      else if (act.Cycle == "Up") lines.push(["Next"]);
      else assertUnreachable(act.Cycle);
    }

    // Led Cycle End
  } else if (isCustomConsumer(act)) {
    // Consumer

    lines.push([kc.displayConsumer(act.Consumer)]);

    // Consumer End
  } else if (isCustomFirmware(act)) {
    // Firmware

    if (act.Firmware == "AllowBootloader") {
      lines.push(["Allow Boot"]);
    } else if (act.Firmware == "JumpToBootloader") {
      lines.push(["Bootloader"]);
    } else if (act.Firmware == "Reboot") {
      lines.push(["Reboot"]);
    } else if (act.Firmware == "InfiniteLoop") {
      lines.push(["Infinite Loop"]);
    } else {
      assertUnreachable(act.Firmware);
    }

    // Firmware End
  } else {
    assertUnreachable(act);
  }

  return lines;
});
</script>
